import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  or,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

export interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  status: 'pending' | 'accepted';
  created_at: string;
  updated_at: string;
  friend?: {
    id: string;
    username: string;
    email: string;
    profile_picture_url?: string;
  };
}

export interface SendFriendRequestRequest {
  friend_id: string;
}

// Helper to convert Firestore timestamp to ISO string
const timestampToISO = (timestamp: Timestamp | Date | string | undefined): string => {
  if (!timestamp) return new Date().toISOString();
  if (timestamp instanceof Date) return timestamp.toISOString();
  if (typeof timestamp === 'string') return timestamp;
  if (timestamp.toDate) return timestamp.toDate().toISOString();
  return new Date().toISOString();
};

export const friendService = {
  getFriends: async (): Promise<Friend[]> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Get friendships where user is either user_id or friend_id and status is accepted
    const q = query(
      collection(db, 'friendships'),
      or(
        where('user_id', '==', user.uid),
        where('friend_id', '==', user.uid)
      )
    );
    const querySnapshot = await getDocs(q);
    const friendships = querySnapshot.docs
      .map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          user_id: data.user_id,
          friend_id: data.friend_id,
          status: data.status as 'pending' | 'accepted',
          created_at: timestampToISO(data.created_at),
          updated_at: timestampToISO(data.updated_at),
        };
      })
      .filter((friendship) => friendship.status === 'accepted') as Friend[];

    // Load user data for each friend
    const friendsWithData = await Promise.all(
      friendships.map(async (friendship) => {
        // Determine which user is the friend (not the current user)
        const friendId = friendship.user_id === user.uid ? friendship.friend_id : friendship.user_id;
        
        try {
          const friendDoc = await getDoc(doc(db, 'users', friendId));
          if (friendDoc.exists()) {
            const friendData = friendDoc.data();
            return {
              ...friendship,
              friend: {
                id: friendDoc.id,
                username: friendData.username || '',
                email: friendData.email || '',
                profile_picture_url: friendData.profile_picture_url,
              },
            };
          }
        } catch (error) {
          console.error(`Error loading friend data for ${friendId}:`, error);
        }
        
        return friendship;
      })
    );

    return friendsWithData;
  },

  sendFriendRequest: async (data: SendFriendRequestRequest): Promise<Friend> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Check if friendship already exists
    const existingQuery = query(
      collection(db, 'friendships'),
      where('user_id', '==', user.uid),
      where('friend_id', '==', data.friend_id)
    );
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error('Friend request already sent');
    }

    const docRef = await addDoc(collection(db, 'friendships'), {
      user_id: user.uid,
      friend_id: data.friend_id,
      status: 'pending',
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    const docSnap = await getDoc(docRef);
    return {
      id: docSnap.id,
      user_id: user.uid,
      friend_id: data.friend_id,
      status: 'pending',
      created_at: timestampToISO(docSnap.data()?.created_at),
      updated_at: timestampToISO(docSnap.data()?.updated_at),
    };
  },

  acceptFriendRequest: async (id: string): Promise<Friend> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const docRef = doc(db, 'friendships', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Friend request not found');

    const data = docSnap.data();
    if (data.friend_id !== user.uid) {
      throw new Error('You can only accept requests sent to you');
    }

    await updateDoc(docRef, {
      status: 'accepted',
      updated_at: serverTimestamp(),
    });

    const updatedDoc = await getDoc(docRef);
    return {
      id: updatedDoc.id,
      ...updatedDoc.data(),
      created_at: timestampToISO(updatedDoc.data()?.created_at),
      updated_at: timestampToISO(updatedDoc.data()?.updated_at),
    } as Friend;
  },

  removeFriend: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'friendships', id));
  },
};
